// dependencies
import { ApolloError } from "@apollo/client";
import produce from "immer";
import create, { GetState, SetState } from "zustand";

import { DataSet, InfoEntry } from "../../../../../components/ColumnPicker";

import {
	ColumnType,
	FilterOptions,
	GridColumn,
	GridSort,
	IAvailableSummarizeField,
	IColumnMetadata,
	SelectedFilter,
	SummaryFunction,
} from "../../../schemas/report";

// hooks
import { getCompanyFeatureFlags } from "../../../../../hooks/useFeatureFlag";

import { getRbacPrimaryRolePermissions } from "../../../../../api/services/defs/rbac";
import { getUserRequiredReportingAnalyticsFilter } from "../../../../../api/services/defs/reporting-analytics";

// utils
import getNested from "../../../../../utils/GetNested";
import { showError, showSuccess } from "../../../../../utils/RequestReponse";
import {
	calculateAvailableSummaryFunctions,
	calculateIsColumnVisible,
	formatSummarizeByAndAggregationOperations,
	getQuickFilterCount,
	getSortFieldPath,
	getSummarizeBySelectOptions,
	getSummarizedViewSortFieldPath,
	selectCurrentSummaryFunction,
	selectNonEmptyFilters,
	selectQuestionTypeDisplayName,
} from "../utils";

import { defaultReportPageSize } from "../../../config";
// Config
import Config from "../Config";
import { ReportAnalyticsRbacPermissions } from "./enums";
import { translateQuickFilterToAdvancedFilter } from "./filters";
// Schemas
import { ReportStore, UIState } from "./model";
// Endpoints
import {
	fetchEmployeeFields,
	fetchEmployeeReportGridData,
	fetchGoalReportGridData,
	fetchReportCSV,
	fetchReviewReportGridData,
	fetchSavedQuery,
	fetchSurveyReportGridData,
	requestEmployeeReportGridDataCount,
	requestGoalReportGridDataCount,
	requestReviewReportGridDataCount,
	requestSurveyReportGridDataCount,
	saveAsReportQuery,
	updateReportQuery,
} from "./requests";
// stores
import chartStore from "./slices/chartsStore";
import employeeReportStore from "./slices/employeeReportStore";
import goalReportStore from "./slices/goalReportStore";
import reviewReportStore from "./slices/reviewReportStore";
import surveyReportStore from "./slices/surveyReportStore";

const valueGetter = (params) => {
	const { data, colDef } = params;

	return getNested(data, colDef.fieldPath, "");
};

const initColumnMetaData = (columns): { [key: string]: IColumnMetadata } => {
	const metaData = columns.reduce((acc, col) => {
		acc[col] = Config.defaultColumnMetaData;
		return acc;
	}, {});

	return metaData;
};

const store = (set: SetState<ReportStore>, get: GetState<ReportStore>) => ({
	/**
	 * FEATURE: UI States
	 */
	uiState: UIState.INITIALIZING,
	activeModal: null,
	hasChanges: false,

	getHasChanges: () => get().hasChanges,
	setActiveModal: (activeModal) => set({ activeModal }),
	setHasChanges: (hasChanges) => set(() => ({ hasChanges })),
	setUIState: (uiState) => set({ uiState }),

	/**
	 * FEATURE: GraphQL Clients/Utils
	 */
	client: null,
	setClient: (client) => set({ client }),

	/**
	 * FEATURE: Report Data
	 */
	report: null,
	reportID: null,

	setReportID: (reportID) => set(() => ({ reportID })),

	getInitialColumns: () => {
		const reportType = get().report?.type || "";
		return Config.initialColumns[reportType] || [];
	},

	/**
	 * FEATURE: Pagination
	 */
	pagination: { pageSize: defaultReportPageSize, pageNumber: 1 },

	/**
	 * Feature Flags
	 */
	companyFeatureFlags: [],

	userPermissions: [],
	setUserPermissions: (userPermissions) => set({ userPermissions }),

	calculateShowAllColumnsToggleValue: () => {
		// if the toggle has not been manually pressed, calculate the value based on if there are any aggregatable columns
		const { getEnabledGridColumns, showAllAggregationColumns } = get();
		let value = showAllAggregationColumns;
		if (typeof showAllAggregationColumns !== "boolean") {
			const enabledColumns = getEnabledGridColumns();
			const aggregatableTypes = [...Config.numericTypes, ...Config.selectTypes];
			const hasAggregatableColumn = enabledColumns.some((column) =>
				aggregatableTypes.includes(column.fieldValueType || "")
			);
			value = hasAggregatableColumn ? false : true;
		}
		return value as boolean;
	},

	sharedWith: [],
	setSharedWith: (sharedWith: string[]) => set({ sharedWith }),

	setPagination: (pagination: { pageNumber: number; pageSize: number }) => set({ pagination }),
	fetchReport: async () => {
		const { client, reportID, setUIState, availableSummarizeFields, setUserPermissions, fetchAvailableColumns } =
			get();

		const companyFeatureFlags = (await getCompanyFeatureFlags()).feature_flags
			.filter((flag) => flag.value === true)
			.map((flag) => flag._id);
		set({ companyFeatureFlags: companyFeatureFlags });

		const user_permissions = await getRbacPrimaryRolePermissions("report_analytics");
		setUserPermissions(user_permissions);

		if (!client) throw new Error("Client not initialized");
		if (!reportID) throw new Error("Report ID not initialized");

		// First fetch the saved query
		const report = await fetchSavedQuery(client, reportID);

		if (!report || report?.deletedAt) {
			setUIState(UIState.ERROR);
			return;
		}

		// display no data if report is not shared with managers or contributors and non-admin is viewing
		// exception is basic-admin and employee reports
		if (
			!user_permissions.includes(ReportAnalyticsRbacPermissions.CreateReport) &&
			!(report?.sharedWith?.includes("managers") || report?.sharedWith?.includes("contributors")) &&
			!(
				user_permissions?.includes(ReportAnalyticsRbacPermissions.CreateEmployeeReport) &&
				report?.type === "employeeReport"
			)
		) {
			setUIState(UIState.ERROR);
			return;
		}

		// if user is not admin, call to get requiredReportingAnalyticsFilter which returns
		// subjectId filter to send to reporting-webserver
		if (
			!user_permissions.includes(ReportAnalyticsRbacPermissions.CreateReport) &&
			!(
				user_permissions?.includes(ReportAnalyticsRbacPermissions.CreateEmployeeReport) &&
				report?.type === "employeeReport"
			)
		) {
			let sharedWithString = "";
			if (report?.sharedWith?.includes("contributors") && report?.sharedWith?.includes("managers")) {
				sharedWithString = "sharedWithManagersAndContributors";
			} else if (report?.sharedWith?.includes("managers")) {
				sharedWithString = "sharedWithManagers";
			} else if (report?.sharedWith?.includes("contributors")) {
				sharedWithString = "sharedWithContributors";
			}
			const requiredReportingAnalyticsFilter: SelectedFilter = await getUserRequiredReportingAnalyticsFilter(
				report?.type,
				sharedWithString
			);

			// managers and contributors must have direct reports/contributes to users in filter
			if (!requiredReportingAnalyticsFilter) {
				setUIState(UIState.ERROR);
				return;
			}
			report.quickFilter.push(requiredReportingAnalyticsFilter);
		}

		if (report?.type === "goalReport" && !companyFeatureFlags.includes("ff-is-goals-enabled")) {
			setUIState(UIState.ERROR);
			return;
		}

		if (report?.type === "surveyReport" && !companyFeatureFlags.includes("ff-is-surveys-enabled")) {
			setUIState(UIState.ERROR);
			return;
		}

		const enabledSummarizeFields = report.summarizeBy
			? report.summarizeBy.map((enabledField) => {
					const fieldLabel = availableSummarizeFields.find(
						(availableField) => availableField.value === enabledField
					)?.label;
					const selection = report.summarizeByMetadata[enabledField]?.selection || null;
					return { value: enabledField, label: fieldLabel, selection };
				})
			: [];

		set({
			report,
			sorts: report.sorts,
			sharedWith: report.sharedWith ?? [],
			enabledGridColumns: report.selectedColumns || Config.initialColumns[report.type],
			selectedQuickFilter: report.quickFilter,
			appliedFiltersCount: getQuickFilterCount(report.quickFilter) + (report?.filters?.length ?? 0),
			...(report.columnMetadata && { columnMetadata: report.columnMetadata }),
			enabledSummarizeFields: enabledSummarizeFields,
			summaryFunctions: report.aggregationOperations,
			isSummarizeRowsView: report.isSummarizeRowsView,
			showAllAggregationColumns:
				typeof report.showAllAggregationColumns === "boolean" ? report.showAllAggregationColumns : null,
			visualizeOptions: report.visualizeOptions,
			pagination: { pageNumber: 1, pageSize: report.pageSize },
		});

		await fetchAvailableColumns();

		// Need a separate call to handle an UI issue with Grid Table
		if (report.visualizeOptions?.selectedChart) {
			const selectedChart = report.visualizeOptions.selectedChart;

			set({ selectedChart, previousChart: report.visualizeOptions.selectedChart });
		}
	},

	setReportFields: (updates) => {
		set(
			produce((draft) => {
				updates.forEach(({ field, value }) => {
					draft.report[field] = value;
				});
			})
		);

		set({ hasChanges: true });
	},
	saveReport: async () => {
		const { client, report, enabledSummarizeFields, pagination, reportID } = get();

		if (!client) throw new Error("Client not initialized");
		if (!report) throw new Error("Report not initialized");
		if (!reportID) throw new Error("Report ID not initialized");
		if (!report?.type) throw new Error("Report type not found");

		try {
			const reportData = get().getSaveReportData();
			const res = await updateReportQuery(client, {
				queryID: reportID,
				savedQuery: {
					type: report.type,
					name: report.name,
					description: report.description,
					tags: report.tags,
					conditions: reportData.filters,
				},
				selectedColumns: reportData.enabledColumns ?? [],
				columnMetadata: reportData.columnMetadata,
				quickFilter: get().selectedQuickFilter,
				sorts: get().sorts,
				summarizeBy: enabledSummarizeFields.map((field) => field.value),
				aggregationOperations: get().summaryFunctions,
				isSummarizeRowsView: get().isSummarizeRowsView,
				showAllAggregationColumns: get().showAllAggregationColumns as boolean,
				summaryColumnMetadata: get().getSummaryColumnMetadata(),
				summarizeByMetadata: enabledSummarizeFields.reduce((acc, field) => {
					acc[field.value] = { selection: field.selection };
					return acc;
				}, {}),
				visualizeOptions: { ...get().getSavedVisualizeOptions(), selectedChart: get().selectedChart },
				pageSize: pagination.pageSize,
				sharedWith: reportData?.sharedWith,
			});

			if (res?.data.updateSavedReviewReportQuery) {
				showSuccess();
				get().setHasChanges(false);
				get().fetchGridDataCount(true);
				get().fetchReport();
			}
		} catch (error) {
			if (error instanceof ApolloError) {
				showError(error.message);
			} else {
				showError("Could not save report");
			}
		}
	},
	/**
	 * This function copies the current report and saves it as a new report. It
	 * uses the same mutation to create a new report.
	 */
	saveAsReport: async (modalInfo) => {
		{
			try {
				const reportData = get().getSaveReportData();
				const enabledSummarizeFields = get().enabledSummarizeFields;
				const { client, pagination } = get();
				const reportType = get().report?.type;

				if (!client) throw new Error("Client not initialized");
				if (!reportType) throw new Error("Report type not found");

				const questionGroupingSessionID = get().report?.questionGroupingSessionID;
				const savedQueryResult = await saveAsReportQuery(client, {
					employeeQueryID: null,
					savedQuery: {
						type: reportType,
						name: modalInfo.name,
						description: modalInfo.description,
						conditions: reportData.filters,
						tags: modalInfo.tags,
					},
					selectedColumns: reportData.enabledColumns ?? [],
					columnMetadata: reportData.columnMetadata,
					quickFilter: get().selectedQuickFilter,
					sorts: get().sorts,
					summarizeBy: enabledSummarizeFields.map((field) => field.value),
					aggregationOperations: get().summaryFunctions,
					isSummarizeRowsView: get().isSummarizeRowsView,
					showAllAggregationColumns: get().showAllAggregationColumns as boolean,
					summaryColumnMetadata: get().getSummaryColumnMetadata(),
					questionGroupingSessionID: questionGroupingSessionID,
					summarizeByMetadata: enabledSummarizeFields.reduce((acc, field) => {
						acc[field.value] = { selection: field.selection };
						return acc;
					}, {}),
					visualizeOptions: { ...get().getSavedVisualizeOptions(), selectedChart: get().selectedChart },
					pageSize: pagination.pageSize,
				});

				const newReportID = savedQueryResult?.data.createSavedReviewReportQuery.savedReviewReportQueryID;

				// we know if it is successful if we have a new report id
				if (!newReportID) {
					throw new Error("Failed to save report");
				}

				showSuccess();
				get().setHasChanges(false);
				get().setReportID(newReportID);
				get().fetchGridDataCount(true);
				get().fetchReport();

				// Put the new reportID in the URL without reloading the entire page
				window.location.href = `/analytics/report/${newReportID}`;
			} catch (error) {
				showError("Unable to save as a new report.");
			}
		}
	},

	getSaveReportData: () => {
		const state = get();

		return {
			name: state.report?.name,
			filters: state.report?.filters ?? [],
			enabledColumns: state.getEnabledGridColumns().map((column) => column.field),
			columnMetadata: state.getEnabledGridColumns().reduce((acc, column) => {
				acc[column.field] = column.columnMetadata;
				return acc;
			}, {}),
			sorts: state.sorts,
			sharedWith: state.sharedWith,
		};
	},

	/**
	 * FEATURE: Grid Data
	 */
	gridData: [],
	gridDataCount: 0,
	// For grid columns we use the terms 'available' and 'enabled'. The
	// available columns are the columns that are available to be enabled.
	// The enabled columns are the columns that are currently enabled and will
	// be displayed in the grid and show a checkbox in the column picker.
	availableGridColumns: [],
	enabledGridColumns: [],
	columnMetadata: {},
	// This is stores a memoized version of the available grid columns and
	// allows for quick access to the values.
	_availableGridColumnsMap: null,
	isSummarizeRowsView: false,

	fetchAvailableSummarizeFields: async () => {
		set({ uiState: UIState.FETCHING });
		await get().fetchGroupedQuestions();
		let allSummarizeFields: IAvailableSummarizeField[] = [];

		// if review report type, fetch review report available fields
		if (get().report?.type === "reviewReport") {
			allSummarizeFields = await get().fetchAvailableReviewSummarizeFields();
		} else if (get().report?.type === "goalReport") {
			allSummarizeFields = await get().fetchAvailableGoalSummarizeFields();
		} else if (get().report?.type === "employeeReport") {
			allSummarizeFields = get().getAvailableEmployeeSummarizeFields();
		} else if (get().report?.type === "surveyReport") {
			allSummarizeFields = get().getAvailableSurveySummarizeFields();
		}

		// after we've fetched all summarizeFields, add the label to the enabled fields
		const enabledSummarizeFields = get().enabledSummarizeFields.map((enabledField) => {
			return {
				...enabledField,
				label: allSummarizeFields.find((field) => field?.value === enabledField.value)?.label || "",
			};
		});

		set({ availableSummarizeFields: allSummarizeFields, enabledSummarizeFields });
	},

	availableSummarizeFields: [],
	enabledSummarizeFields: [],
	summaryFunctions: {},
	showAllAggregationColumns: null,
	sorts: [],
	canViewSummary: undefined,

	fetchGridData: async (forceRefetch?: boolean, abortController?: any) => {
		const {
			client,
			report,
			getEnabledGridColumns,
			getFilterOptions,
			sorts,
			pagination,
			setUIState,
			isSummarizeRowsView,
			enabledSummarizeFields,
			getSummaryColumnMetadata,
			expandedSuperRowCell,
			setExpandedSuperRowCell,
		} = get();

		setUIState(UIState.FETCHING);

		// close any opened super row
		if (expandedSuperRowCell) {
			setExpandedSuperRowCell(null);
		}

		if (!client) return;

		const columns = await getEnabledGridColumns().map((column) => {
			const type = column.fieldType;
			const columnID = type === "EMPLOYEE_FIELD" ? column.headerName : column.field;

			return { type, id: columnID };
		});
		const filters = get().getAppliedFilters();

		let aggregationOperations: { type: any; name: string; operation: SummaryFunction }[] = [];
		let summarizeBy = [];
		const allowedSorts = sorts;

		if (!isSummarizeRowsView) {
			set({ canViewSummary: false });
		}

		if (isSummarizeRowsView) {
			await get().fetchCanViewSummary();
			const canViewSummary = await get().canViewSummary;
			if (canViewSummary) {
				const summaryFunctions = getSummaryColumnMetadata();

				const { summarizeBy: formattedSummarizeBy, aggregationOperations: formattedAggregationOperations } =
					formatSummarizeByAndAggregationOperations(enabledSummarizeFields, summaryFunctions);

				summarizeBy = formattedSummarizeBy;
				aggregationOperations = formattedAggregationOperations;
			}
		}

		// if report type is review report, fetch review report grid data
		// otherwise, fetch employee report grid data

		if (report?.type === "reviewReport") {
			await Promise.all([get().fetchQuestions(), get().fetchGroupedQuestions()]);

			const query = await fetchReviewReportGridData(
				client,
				report?.questionGroupingSessionID || "",
				columns,
				filters,
				allowedSorts,
				pagination,
				forceRefetch === true,
				summarizeBy,
				aggregationOperations
			);

			const observable = query?.subscribe({
				next: ({ data }) => {
					const augmentedGridData = data.reviewReports.map((row) => {
						const {
							questions,
							questions_map,
							question_group_map,
							review_form,
							review_report,
							review,
							...rest
						} = row;

						return {
							...rest,
							questions: questions_map,
							groupedQuestions: question_group_map,
							// combine review_form and review_report data to make Review Form Fields section
							review_form: { name: review_form?.name || "", ...review_report },
							review: {
								...review,
								forms_awaiting_approval: isSummarizeRowsView
									? review?.forms_awaiting_approval
									: review?.forms_awaiting_approval?.length || 0,
							},
						};
					});

					set({ gridData: augmentedGridData });
					setUIState(UIState.READY);
				},
			});

			// if we have an abort controller, we want to unsubscribe when the abort signal is fired
			if (abortController) {
				abortController.signal.addEventListener("abort", () => {
					observable?.unsubscribe();
				});
			}
		} else if (report?.type === "employeeReport") {
			const query = await fetchEmployeeReportGridData(
				client,
				columns,
				filters,
				allowedSorts,
				pagination,
				forceRefetch === true,
				summarizeBy,
				aggregationOperations
			);

			const observable = query?.subscribe({
				next: ({ data }) => {
					set({ gridData: data.employeeReports });
					setUIState(UIState.READY);
				},
			});

			// if we have an abort controller, we want to unsubscribe when the abort signal is fired
			if (abortController) {
				abortController.signal.addEventListener("abort", () => {
					observable?.unsubscribe();
				});
			}
		} else if (report?.type === "goalReport") {
			// fetch goal report grid data
			const query = await fetchGoalReportGridData(
				client,
				columns,
				filters,
				allowedSorts,
				pagination,
				forceRefetch === true,
				summarizeBy,
				aggregationOperations
			);

			const observable = query?.subscribe({
				next: ({ data }) => {
					set({ gridData: data.goalReports });
					setUIState(UIState.READY);
				},
			});

			// if we have an abort controller, we want to unsubscribe when the abort signal is fired
			if (abortController) {
				abortController.signal.addEventListener("abort", () => {
					observable?.unsubscribe();
				});
			}
		} else if (report?.type === "surveyReport") {
			await Promise.all([get().fetchSurveyQuestions(), get().fetchGroupedSurveyQuestions()]);
			// fetch goal report grid data
			const query = await fetchSurveyReportGridData(
				client,
				columns,
				report?.questionGroupingSessionID || "",
				filters,
				allowedSorts,
				pagination,
				forceRefetch === true,
				summarizeBy,
				aggregationOperations
			);

			const observable = query?.subscribe({
				next: ({ data }) => {
					const augmentedGridData = data.surveyReports.map((row) => {
						const { questions_map, question_group_map, ...rest } = row;

						return {
							...rest,
							questions: questions_map,
							groupedQuestions: question_group_map,
						};
					});

					set({ gridData: augmentedGridData });
					setUIState(UIState.READY);
				},
			});
			// if we have an abort controller, we want to unsubscribe when the abort signal is fired
			if (abortController) {
				abortController.signal.addEventListener("abort", () => {
					observable?.unsubscribe();
				});
			}
		}

		const filterOptions = await getFilterOptions();
		set({ filterOptions });
	},

	fetchGridDataCount: async (forceRefetch?: boolean) => {
		const {
			client,
			report,
			getEnabledGridColumns,
			sorts,
			isSummarizeRowsView,
			getSummaryColumnMetadata,
			enabledSummarizeFields,
		} = get();

		if (!client) throw new Error("Client not initialized");
		if (!report) throw new Error("Report not initialized");

		const columns = getEnabledGridColumns().map((column) => {
			const type = column.fieldType;
			const columnID = type === "EMPLOYEE_FIELD" ? column.headerName : column.field;

			return { type, id: columnID };
		});

		let aggregationOperations: { type: any; name: string; operation: SummaryFunction }[] = [];
		let summarizeBy = [];

		if (isSummarizeRowsView) {
			const summaryFunctions = getSummaryColumnMetadata();
			const { summarizeBy: formattedSummarizeBy, aggregationOperations: formattedAggregationOperations } =
				formatSummarizeByAndAggregationOperations(enabledSummarizeFields, summaryFunctions);
			summarizeBy = formattedSummarizeBy;
			aggregationOperations = formattedAggregationOperations;
		}

		const filters = get().getAppliedFilters();
		let gridDataCount = 0;

		// if report type is review report, fetch review report grid data count
		// otherwise, fetch employee report grid data count
		if (report?.type === "reviewReport") {
			gridDataCount = await requestReviewReportGridDataCount(
				client,
				report.questionGroupingSessionID ?? "",
				columns,
				filters,
				sorts,
				forceRefetch === true,
				summarizeBy,
				aggregationOperations
			);
		} else if (report?.type === "employeeReport") {
			gridDataCount = await requestEmployeeReportGridDataCount(
				client,
				columns,
				filters,
				sorts,
				forceRefetch === true,
				summarizeBy,
				aggregationOperations
			);
		} else if (report?.type === "goalReport") {
			gridDataCount = await requestGoalReportGridDataCount(
				client,
				columns,
				filters,
				sorts,
				forceRefetch === true,
				summarizeBy,
				aggregationOperations
			);
		} else if (report?.type === "surveyReport") {
			gridDataCount = await requestSurveyReportGridDataCount(
				client,
				columns,
				filters,
				sorts,
				forceRefetch === true,
				summarizeBy,
				aggregationOperations,
				report.questionGroupingSessionID ?? ""
			);
		}
		set({ gridDataCount });
	},

	_formatGridSorts: (sorts) => {
		const { _availableGridColumnsMap, getEnabledSummarizeRowsColumns, isSummarizeRowsView } = get();

		const enabledSummarizeRowColumns = getEnabledSummarizeRowsColumns();

		const gridSorts = sorts
			.map((sort) => {
				const isSummarySort = sort.colId.split("_")[0] === "summary";

				// look up column info from sort id
				// depending on summary or non-summary item
				// default to return null if not found
				let gridColumn = _availableGridColumnsMap?.[sort.colId];

				if (isSummarySort) {
					gridColumn = enabledSummarizeRowColumns.find((column) => column.field === sort.colId);
				}
				if (!gridColumn) {
					return null;
				}
				return {
					type: gridColumn.fieldType as GridSort["type"],
					name: gridColumn.field.split("_").slice(1).join("_"),
					fieldPath: isSummarizeRowsView
						? getSummarizedViewSortFieldPath(gridColumn)
						: getSortFieldPath(gridColumn),
					direction: sort.direction === "asc" ? "asc" : ("desc" as GridSort["direction"]),
				};
			})
			.filter((dd) => !!dd);

		return gridSorts;
	},

	// sorts come from the grid and need to be translated into a GridSort
	// type which is how the server expects it. In order to do this, we need
	// to get the column type from the column id.
	setSorts: (sorts) => {
		const { _formatGridSorts } = get();
		// close any opened super row
		if (get().expandedSuperRowCell) {
			get().setExpandedSuperRowCell(null);
		}

		set({ sorts: _formatGridSorts(sorts) });
	},

	getGridSorts: () => {
		const { sorts } = get();

		return sorts.map((sort, ii) => {
			const { type, name, direction, fieldPath } = sort;

			if (fieldPath && fieldPath[0] && fieldPath[0] === "summarizeBy") {
				return { colId: `summary_${name}`, sort: direction, sortIndex: ii };
			}

			const columnType = ColumnType[type] || "";

			return { colId: `${columnType}_${name}`, sort: direction, sortIndex: ii };
		});
	},

	/**
	 * Validations and filtering to run on sorts before sending to the server,
	 * because some sorts combinations are not allowed.
	 */
	validateAndFilterSorts: (sorts) => {
		// ensures there is only 1 sort of type "people", or other array field type. removes extras from the list, leaving only the most recently selected
		// this has the effect of enabling the user to "switch" from one type of people sort to another using the same type of click action
		const arrayFieldTypes = ["goalCategories", "people"];
		const { _availableGridColumnsMap, userPermissions } = get();
		const colTypes = sorts.map(
			(sort) => _availableGridColumnsMap?.[sort.colId.replace("summary_", "")]?.fieldValueType
		);
		const includesFormAuthorsField = sorts.some((sort) => sort.colId === "reviewform_authors");
		if (!userPermissions.includes(ReportAnalyticsRbacPermissions.CreateReport) && includesFormAuthorsField) {
			// in this case, if form authors is the last selected sort, remove all other sorts
			if (sorts[sorts.length - 1].colId === "reviewform_authors") {
				return sorts.filter((sort) => sort.colId === "reviewform_authors");
			}
			// otherwise remove all form authors sorts
			return sorts.filter((sort) => sort.colId !== "reviewform_authors");
		}
		if (colTypes.filter((type) => arrayFieldTypes.includes(type)).length > 1) {
			// remove all array field sorts except the last one
			const lastArrayFieldSortIndex = sorts
				.map((sort, index) => ({ sort, index }))
				.filter((sort) => arrayFieldTypes.includes(colTypes[sort.index]))
				.pop()?.index;
			return sorts.filter(
				(sort, index) => !arrayFieldTypes.includes(colTypes[index]) || index === lastArrayFieldSortIndex
			);
		}
		return sorts;
	},

	setIsSummarizeRowsView: (isSummarizeRowsView) => {
		const { fallbackToTableChart } = get();
		set({ isSummarizeRowsView });
		// fallback to table chart if summarize rows view is disabled
		if (!isSummarizeRowsView) {
			fallbackToTableChart();
		}
	},

	setAvailableSummarizeFields: (availableSummarizeFields) => set({ availableSummarizeFields }),
	setEnabledSummarizeFields: (enabledSummarizeFields) => set({ enabledSummarizeFields, hasChanges: true }),
	setSummaryFunctions: (summaryFunctions: any) => set({ summaryFunctions, hasChanges: true }),

	setShowAllAggregationColumns: (showAllAggregationColumns: boolean) => {
		set({ showAllAggregationColumns, hasChanges: true });
		get().fetchGridData();
	},
	setCanViewSummary: (canViewSummary: boolean) => set({ canViewSummary }),

	// returns all summary functions for enabled columns,
	// if no summary function, set the default to average or count (depending on column type)
	getSummaryColumnMetadata: () => {
		const {
			isSummarizeRowsView,
			enabledGridColumns,
			summaryFunctions,
			enabledSummarizeFields,
			availableGridColumns,
			calculateShowAllColumnsToggleValue,
		} = get();

		const getMetaDataInfo = (column, columnGroup, columnInfo) => {
			return {
				isColumnVisible: calculateIsColumnVisible(
					isSummarizeRowsView,
					column,
					calculateShowAllColumnsToggleValue(),
					enabledSummarizeFields,
					columnInfo,
					columnGroup
				),
				currentlySelectedSummaryFunction: selectCurrentSummaryFunction(
					isSummarizeRowsView,
					column,
					summaryFunctions,
					enabledSummarizeFields,
					columnInfo,
					columnGroup
				),
				availableSummaryFunctions: calculateAvailableSummaryFunctions(
					isSummarizeRowsView,
					enabledSummarizeFields,
					columnInfo,
					columnGroup
				),
			};
		};

		const enabledSummaryFunctions = enabledGridColumns.reduce((acc, column) => {
			const columnInfo = availableGridColumns.find((availableColumn) => availableColumn.field === column);
			const columnGroup = columnInfo?.field.split("_")[0] || "";
			acc[column] = getMetaDataInfo(column, columnGroup, columnInfo);
			return acc;
		}, {});

		return enabledSummaryFunctions;
	},

	resetColumnsToDefault: () => {
		set({
			enabledGridColumns: get().getInitialColumns(),
			columnMetadata: initColumnMetaData(get().getInitialColumns()),
		});
	},
	fetchCanViewSummary: async () => {
		const {
			enabledSummarizeFields,
			setSorts,
			setEnabledSummarizeFields,
			getSummaryColumnMetadata,
			client,
			report,
		} = get();

		if (!client) throw new Error("Client not initialized");

		const employeeFields = await fetchEmployeeFields(client, report);
		const employeeFieldNames = employeeFields.map((field) => field.name);
		const user_permissions = await getRbacPrimaryRolePermissions("report_analytics");
		if (user_permissions?.includes(ReportAnalyticsRbacPermissions.CreateReport)) {
			set({ canViewSummary: true });
		} else {
			const summaryFunctions = getSummaryColumnMetadata();
			const { summarizeBy: formattedSummarizeBy } = formatSummarizeByAndAggregationOperations(
				enabledSummarizeFields,
				summaryFunctions
			);

			const canViewSummary =
				formattedSummarizeBy?.length > 0 &&
				formattedSummarizeBy?.every((column) => {
					if (column.type === "EMPLOYEE_FIELD") {
						return employeeFieldNames.includes(column.name);
					}
					// only filter on employee_fields which have visibility settings
					return true;
				});
			set({ canViewSummary });
			if (canViewSummary === false) {
				setEnabledSummarizeFields([]);
				setSorts([]);
			}
		}
	},

	// This method fetches all available columns. These columns come from four
	// different sources: employee fields, review fields (these are hardcoded),
	// unique review report questions, and grouped questions.
	fetchAvailableColumns: async () => {
		let availableGridColumns: GridColumn[] = [];

		// if review report type, fetch review report available fields
		const reportType = get().report?.type || "";
		if (reportType === "reviewReport") {
			availableGridColumns = await get().fetchAvailableReviewColumns();
		} else if (reportType === "employeeReport") {
			availableGridColumns = await get().fetchAvailableEmployeeColumns();
		} else if (reportType === "goalReport") {
			availableGridColumns = await get().fetchAvailableGoalColumns();
		} else if (reportType === "surveyReport") {
			availableGridColumns = await get().fetchAvailableSurveyColumns();
		}
		// create a map for quick access in the `getEnabledGridColumns` function
		const availableGridColumnsMap = availableGridColumns.reduce((acc, col) => {
			acc[col.field] = col;
			return acc;
		}, {});

		set({ availableGridColumns, _availableGridColumnsMap: availableGridColumnsMap });
	},

	// A dumb state updater of the enabled columns array
	setEnabledGridColumns: (enabledGridColumns) => {
		set({ enabledGridColumns, hasChanges: true });
	},

	setColumnMetaData: (columnMetadata) => {
		set({ columnMetadata, hasChanges: true });
	},

	// Add a column to the enabled columns array in the appropriate order
	_addColumnToEnabledGridColumns: (columnID) => {
		const addOnLeftColumns = ["review_subject"];
		const columnType = columnID.split("_")[0];
		const isEmployeeField = columnType === "employee";

		let newEnabledGridColumns;
		// all employee fields get added on the left
		if (isEmployeeField) {
			newEnabledGridColumns = [columnID, ...get().enabledGridColumns];
		}
		// Certain columns should be added on the left
		else if (addOnLeftColumns.indexOf(columnID) !== -1) {
			newEnabledGridColumns = [columnID, ...get().enabledGridColumns];
		}
		// Default case add it on the right
		else {
			newEnabledGridColumns = [...get().enabledGridColumns, columnID];
		}
		get().setEnabledGridColumns(newEnabledGridColumns);
	},

	// Handles logic for toggling columns on or off. Right now new columns are
	// added to the end of the array.
	toggleGridColumn: (columnID) => {
		const { enabledGridColumns, _addColumnToEnabledGridColumns, isSummarizeRowsView, fetchGridData } = get();

		const action = enabledGridColumns.includes(columnID) ? "remove" : "add";

		// close any opened super row
		if (get().expandedSuperRowCell) {
			get().setExpandedSuperRowCell(null);
		}

		if (action === "add") {
			_addColumnToEnabledGridColumns(columnID);

			// if we are in summarize rows view, we need to update the newly enabled
			// columns with summarized data
			if (isSummarizeRowsView) {
				fetchGridData(true);
			}
		} else {
			get().setEnabledGridColumns(enabledGridColumns.filter((column) => column !== columnID));
		}
	},

	// Returns all enabled grid columns as a GridColumn type
	getEnabledGridColumns: () => {
		const state = get();
		const { _availableGridColumnsMap } = state;
		const { enabledGridColumns, columnMetadata } = state;
		if (!_availableGridColumnsMap) {
			return [];
		}

		const enabledGridColumnsAugmented = enabledGridColumns.map((col) => {
			const definition = _availableGridColumnsMap[col];

			if (!definition) {
				return null;
			}

			const metaData = { ...(definition?.columnMetadata || {}), ...columnMetadata[col] };

			return {
				...{ valueGetter },
				...definition,
				columnMetadata: metaData,
				isColumnVisible: false,
				defaultAggregationFunction: "count",
			};
		});

		return enabledGridColumnsAugmented.filter((col) => !!col) as GridColumn[];
	},

	getEnabledSummarizeRowsColumns: () => {
		const state = get();
		const { enabledSummarizeFields, availableSummarizeFields, columnMetadata } = state;
		const enabledSummarizeFieldsAugmented = enabledSummarizeFields.map((field) => {
			// field.value is formatted as fieldgroup_fieldname
			// the regex takes the first part of the string before the underscore
			// and uses it as the field group and uses the second part as the field name
			// ex: reviewcycle_name => fieldGroup = reviewcycle, fieldName = name

			const fieldGroup = field.value.split(/_(.*)/s)[0];
			const fieldName = field.value.split(/_(.*)/s)[1];
			const availableField = availableSummarizeFields.find(
				(availableField) => availableField.value === field.value
			);
			const fieldType = availableField ? availableField.type : null;

			return {
				columnMetadata: columnMetadata[field.value],
				isSortable: true,
				field: `summary_${field.value}`,
				fieldPath: ["summarizeBy", Config.fieldGroupLookup[fieldGroup], fieldName, "name"],
				fieldType: Config.fieldGroupLookup[fieldGroup],
				fieldValueType: fieldType,
				headerName: field.label,
				pinned: null,
				resizable: true,
				headerTooltip: field.label,
				selection: field.selection,
				selectionOptions: getSummarizeBySelectOptions(fieldType),
			};
		});
		return enabledSummarizeFieldsAugmented.filter((col) => !!col);
	},

	fetchEmployeeFields: async () => {
		const { client, report } = get();
		if (!client) throw new Error("Client not initialized");

		const employeeFields = await fetchEmployeeFields(client, report);

		set({ employeeFields });
	},

	// Returns all available grid columns as a DataSet type. This is the
	// expected type to be fed into the <ColumnPicker /> component.
	// TODO - possibly memoize this return value since the inputs rarely change
	getAvailableGridColumns: () => {
		const { availableGridColumns: columns, report } = get();
		const datasetOrder = [
			"EMPLOYEE_FIELD",
			"GOAL_FIELD",
			"REVIEW_CYCLE_FIELD",
			"REVIEW_FIELD",
			"REVIEW_FORM_FIELD",
			"UNIQUE_QUESTION",
			"SURVEY_FIELD",
			"SURVEY_QUESTION",
			"GROUPED_QUESTION",
		];

		const datasets: { [key: string]: DataSet } = {
			EMPLOYEE_FIELD: { id: "EMPLOYEE_FIELD", label: "Employee Fields", values: [] },
			GOAL_FIELD: { id: "GOAL_FIELD", label: "Goal Fields", values: [] },
			REVIEW_FORM_FIELD: { id: "REVIEW_FORM_FIELD", label: "Review Form Fields", values: [] },
			REVIEW_FIELD: { id: "REVIEW_FIELD", label: "Review Fields", values: [] },
			REVIEW_CYCLE_FIELD: { id: "REVIEW_CYCLE_FIELD", label: "Review Cycle Fields", values: [] },
			UNIQUE_QUESTION: { id: "UNIQUE_QUESTION", label: "Questions", values: [] },
			GROUPED_QUESTION: { id: "GROUPED_QUESTION", label: "Question Groups", values: [] },
			SURVEY_FIELD: { id: "SURVEY_FIELD", label: "Survey Fields", values: [] },
			SURVEY_QUESTION: { id: "SURVEY_QUESTION", label: "Questions", values: [] },
		};

		const formatInfo = (column: GridColumn): InfoEntry[] => {
			let info: InfoEntry[] = [];
			if (column.fieldType === "GOAL_FIELD") {
				info = [{ label: "Type", value: column.fieldValueTypeLabel || column.fieldValueType || "Goal Field" }];
			} else if (column.fieldType === "UNIQUE_QUESTION") {
				const questionID = column.field.split("question_")[1];
				const question = get().uniqueReviewReportQuestionsMap[questionID];
				if (question) {
					info = [
						{ label: "Form", value: question.reviewFormName },
						{ label: "Type", value: selectQuestionTypeDisplayName(question.questionType) },
						{ label: "Question", value: question.question },
					];
				}
			} else if (column.fieldType === "GROUPED_QUESTION") {
				const isReviewReport = report?.type === "reviewReport";
				const nonUniqueQuestionsMap = isReviewReport
					? get().nonUniqueReviewReportQuestionsMap
					: get().nonUniqueSurveyQuestionsMap;
				const questionID = column.field.split("groupedquestion_")[1];
				const group = get().groupedQuestions.find((question) => question.id === questionID);
				if (![null, undefined].includes(group)) {
					info = [
						{ label: "Name", value: group.label },
						{
							label: "Types",
							value: Array.from(
								new Set(
									group.questionsInGroup.map((questionID: string) =>
										selectQuestionTypeDisplayName(nonUniqueQuestionsMap[questionID]?.questionType)
									)
								)
							),
						},
						{
							label: "Forms",
							value: isReviewReport
								? group.questionsInGroup
										.map((questionID: string) => nonUniqueQuestionsMap[questionID]?.reviewFormName)
										.filter((name: string | null) => !!name)
								: group.questionsInGroup
										.map((questionID: string) => nonUniqueQuestionsMap[questionID]?.surveyName)
										.filter((name: string | null) => !!name),
						},
						{
							label: "Questions",
							value: group.questionsInGroup
								.map((questionID: string) => nonUniqueQuestionsMap[questionID]?.question)
								.filter((question: string | null) => !!question),
						},
					];
				}
			} else {
				if (!column.fieldValueType) throw new Error("Column fieldValueType is not defined");

				info = [{ label: "Type", value: column.fieldValueType }];
			}
			return info;
		};

		columns.forEach((col) => {
			const { fieldType } = col;
			const dataset: DataSet = datasets[fieldType];

			// for now, filter out the Has Direct Reports field
			// we only want it as an additional filter field, not as a selectable column
			if (col.headerName === "Has Direct Reports") {
				return;
			}

			if (dataset) {
				dataset.values.push({
					id: col.field,
					label: col.headerName,
					info: formatInfo(col),
					sectionID: fieldType,
				});
			}
		});

		// return datasets in order, filtering out any empty datasets that are not question groups
		return datasetOrder
			.map((datasetID) => datasets[datasetID])
			.filter(
				(dataset) =>
					dataset.values.length > 0 ||
					(dataset.id === "GROUPED_QUESTION" &&
						(report?.type === "reviewReport" || report?.type === "surveyReport"))
			);
	},

	/**
	 * FEATURE: Multi-Select (Super Row)
	 */
	expandedSuperRowCell: null,
	expandingSuperRowCell: null,
	setExpandedSuperRowCell: (expandedSuperRowCell) => set({ expandedSuperRowCell }),
	getExpandedSuperRowCell: () => get().expandedSuperRowCell,
	setExpandingSuperRowCell: (expandingSuperRowCell) => set({ expandingSuperRowCell }),
	getExpandingSuperRowCell: () => get().expandingSuperRowCell,

	/**
	 * FEATURE: Filters
	 */
	appliedFiltersCount: 0,
	filterOptions: null,
	selectedQuickFilter: [],

	setFilterOptions: (filterOptions) => set(() => ({ filterOptions })),

	setSelectedQuickFilter: (quickFilter) => {
		const cleanedQuickFilters = selectNonEmptyFilters(quickFilter);
		set({ selectedQuickFilter: cleanedQuickFilters });

		set({ hasChanges: true });
	},
	setAdditionalFilters: (additionalFilters) => {
		get().setReportFields([{ field: "filters", value: additionalFilters }]);
	},

	getAppliedFilters: () => {
		const selectedQuickFilters = get().selectedQuickFilter;

		const formattedQuickFilters = translateQuickFilterToAdvancedFilter(selectedQuickFilters);

		return [...formattedQuickFilters, ...(get().report?.filters || [])];
	},

	getFilterOptions: async () => {
		const {
			client,
			report,
			fetchReviewFilterOptions,
			fetchEmployeeFilterOptions,
			fetchGoalFilterOptions,
			fetchSurveyFilterOptions,
		} = get();

		if (!client) throw new Error("Client not initialized");

		let filterOptions = {} as FilterOptions;

		// fetch filter options based on review type
		if (report?.type === "reviewReport") {
			filterOptions = await fetchReviewFilterOptions();
		} else if (report?.type === "employeeReport") {
			filterOptions = await fetchEmployeeFilterOptions();
		} else if (report?.type === "goalReport") {
			filterOptions = await fetchGoalFilterOptions();
		} else if (report?.type === "surveyReport") {
			filterOptions = await fetchSurveyFilterOptions();
		}

		return filterOptions;
	},

	applySelectedQuickFilter: () => {
		get().fetchGridData();
		get().fetchGridDataCount(true);
		get().fetchAvailableColumns();

		const quickFiltersCount = getQuickFilterCount(get().selectedQuickFilter);
		const additionalFiltersCount = get().report?.filters?.length ?? 0;

		set({ appliedFiltersCount: quickFiltersCount + additionalFiltersCount });
	},

	/**
	 * FEATURE: CSV
	 */
	csvJobID: null,
	resetCSVJobID: () => set({ csvJobID: null }),
	fetchReportCSV: async () => {
		const {
			client,
			report,
			getEnabledGridColumns,
			sorts,
			isSummarizeRowsView,
			enabledSummarizeFields,
			getSummaryColumnMetadata,
		} = get();

		if (!client) throw new Error("Client not initialized");
		if (!report) throw new Error("Report not initialized");

		const columns = getEnabledGridColumns().map((column) => {
			const type = column.fieldType;
			const columnID = column.field;

			return { type, id: columnID };
		});

		const filters = get().getAppliedFilters();

		let aggregationOperations: { type: any; name: string; operation: SummaryFunction }[] = [];
		let summarizeBy = [];
		let summaryColumnMetadata = {};
		let summarizeByMetadata = {};

		if (isSummarizeRowsView) {
			await get().fetchCanViewSummary();
			const canViewSummary = await get().canViewSummary;
			if (canViewSummary) {
				summaryColumnMetadata = getSummaryColumnMetadata();

				const { summarizeBy: formattedSummarizeBy, aggregationOperations: formattedAggregationOperations } =
					formatSummarizeByAndAggregationOperations(enabledSummarizeFields, summaryColumnMetadata);

				summarizeBy = formattedSummarizeBy;
				aggregationOperations = formattedAggregationOperations;

				summarizeByMetadata = enabledSummarizeFields.reduce((acc, field) => {
					acc[field.value] = { selection: field.selection };
					return acc;
				}, {});
			}
		}

		const csvJobID = await fetchReportCSV(
			client,
			report.questionGroupingSessionID ?? "",
			columns,
			filters,
			sorts,
			report.name,
			summarizeBy,
			aggregationOperations,
			isSummarizeRowsView,
			summaryColumnMetadata,
			summarizeByMetadata
		);

		set({ csvJobID });
	},

	/**
	 * Initializes the report store with the given report ID.
	 * This sets up all necessary data and state for the report.
	 * @param reportID - The ID of the report to initialize
	 */
	init: async (reportID: string) => {
		const {
			client,
			setUIState,
			setReportID,
			fetchReport,
			resetColumnsToDefault,
			fetchAvailableColumns,
			fetchGridData,
			fetchGridDataCount,
			fetchAvailableSummarizeFields,
			pagination,
			setPagination,
		} = get();

		if (!client) {
			throw new Error("Client not initialized");
		}

		try {
			// Set initial state
			setUIState(UIState.INITIALIZING);
			setReportID(reportID);
			resetColumnsToDefault();

			// Fetch initial report data.
			// This is needed to get the report.type
			await fetchReport();

			// Set up pagination with defaults if needed
			const pageSize = pagination.pageSize || defaultReportPageSize;
			const pageNumber = pagination.pageNumber || 1;
			setPagination({ pageSize, pageNumber });

			// Fetch all grid-related data in parallel
			await Promise.all([
				fetchAvailableColumns(),
				fetchGridData(),
				fetchGridDataCount(),
				fetchAvailableSummarizeFields(),
			]);

			setUIState(UIState.READY);
		} catch (error) {
			console.error("Failed to initialize report:", error);
			setUIState(UIState.ERROR);
			throw error;
		}
	},
});

const useStore = create<ReportStore>((set, get) => ({
	...employeeReportStore(set, get),
	...reviewReportStore(set, get),
	...goalReportStore(set, get),
	...surveyReportStore(set, get),
	...store(set, get),
	...chartStore(set, get),
}));

export default useStore;
